import { _IdGenerator } from '@angular/cdk/a11y';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  MORTGAGE_CUSTOMER_SERVICE_DISPLAY,
  MORTGAGE_CUSTOMER_SERVICE_TEL,
  ORIGINATION_SUPPORT_DISPLAY,
  ORIGINATION_SUPPORT_TEL,
} from '@ld.consumer-portal/constants';
import { ShareScreenService } from '@ld.consumer-portal/data-access';
import {
  communicationPhone,
  customerSupportRepresentativeIcon,
  heroiconScreenshare,
  heroiconsContactPhone,
} from '@ld.consumer-portal/data-uri';
import { map } from 'rxjs';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ld-share-screen-card',
  imports: [SvgIconComponent, TranslocoDirective, MatProgressSpinnerModule],
  templateUrl: './share-screen-card.component.html',
  styleUrl: './share-screen-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareScreenCardComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly shareScreenService = inject(ShareScreenService);

  isMobile = input<boolean | undefined>();
  variant = input<'standard' | 'loan-tasks'>('standard');

  private readonly isMobileViewport = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((result) => result.matches)),
    { initialValue: false },
  );
  protected readonly isMobileDisplay = computed(() => this.isMobile() ?? this.isMobileViewport());
  protected readonly isShareScreenLoading = this.shareScreenService.isStarting;

  /** Unique per instance so several cards can render on the same page without duplicate DOM ids. */
  private readonly idPrefix = inject(_IdGenerator).getId('share-screen-card-');
  protected readonly elementIds = {
    card: this.idPrefix,
    header: `${this.idPrefix}-header`,
    icon: `${this.idPrefix}-icon`,
    title: `${this.idPrefix}-title`,
    description: `${this.idPrefix}-description`,
    originationsSection: `${this.idPrefix}-originations-section`,
    servicingSection: `${this.idPrefix}-servicing-section`,
    shareScreenSection: `${this.idPrefix}-share-screen-section`,
    phones: `${this.idPrefix}-phones`,
    originationsPhone: `${this.idPrefix}-originations-phone`,
    servicingPhone: `${this.idPrefix}-servicing-phone`,
    prompt: `${this.idPrefix}-prompt`,
    promptTitle: `${this.idPrefix}-prompt-title`,
    promptDescription: `${this.idPrefix}-prompt-description`,
    button: `${this.idPrefix}-button`,
    buttonIcon: `${this.idPrefix}-button-icon`,
    buttonLabel: `${this.idPrefix}-button-label`,
    spinner: `${this.idPrefix}-spinner`,
  };

  protected readonly communicationPhoneIcon = communicationPhone;
  protected readonly customerSupportIcon = customerSupportRepresentativeIcon;
  protected readonly heroiconScreenshareIcon = heroiconScreenshare;
  protected readonly phoneIcon = heroiconsContactPhone;
  protected readonly originationSupportPhone = ORIGINATION_SUPPORT_DISPLAY;
  protected readonly originationSupportTel = ORIGINATION_SUPPORT_TEL;
  protected readonly servicingSupportPhone = MORTGAGE_CUSTOMER_SERVICE_DISPLAY;
  protected readonly servicingSupportTel = MORTGAGE_CUSTOMER_SERVICE_TEL;
  protected readonly shareScreenCardDescriptionKey = computed(() =>
    this.isMobileDisplay() ? 'shareScreenCardNeedHelpDescription' : 'shareScreenCardOnCallDescription',
  );
  protected readonly shareScreenCardButtonKey = computed(() =>
    this.isMobileDisplay() ? 'shareScreenCardButtonMobile' : 'shareScreenCardButton',
  );
  protected readonly shareScreenCardButtonClass = computed(() =>
    this.isMobileDisplay()
      ? 'inline-flex w-full items-center justify-center gap-2 rounded-[20px] bg-ld-lime px-4 py-[6px] font-dm text-[13px] font-bold uppercase leading-5 tracking-[0.5px] text-ld-neutral-700 transition-colors cursor-pointer hover:bg-ld-lime-hover focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 disabled:cursor-not-allowed'
      : 'inline-flex w-full items-center justify-center gap-2 rounded-[20px] bg-ld-lime px-5 py-2 font-dm text-base font-bold uppercase leading-6 tracking-[0.5px] text-ld-neutral-700 transition-colors cursor-pointer hover:bg-ld-lime-hover focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 disabled:cursor-not-allowed',
  );

  protected onShareScreenClick(): Promise<void> {
    return this.shareScreenService.startSession('ShareScreenCardComponent');
  }
}
